import { ExternalLink, Truck } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Button } from "@/components/ui/button"
import type { Locale } from "@/types"

/**
 * OKS-482 — Tracking block on the shipped state.
 *
 * Degrades gracefully (TRD KD-5, no carrier integration):
 *   - `tracking_number` present → "Tracking #…" + optional "Arrives {eta}"
 *     + a `Track →` link/button ONLY when `tracking_url` is present (https).
 *     No `tracking_url` → text only, never a dead `#` link.
 *   - `tracking_number` blank but `tracking_url` present → "Shipped" plus a
 *     WORKING Track link. No number to show, but there is something to track.
 *   - both blank → generic "Shipped — no tracking available".
 *
 * ⚠️ OKS-984 (PRD OQ-8) fixed the middle case. This component previously
 * early-returned "no tracking available" on `!trackingNumber` ALONE, so a
 * shipment with a real carrier URL and no derived number rendered as
 * untrackable and offered no link — the creator was told nothing existed while
 * the brand could see the URL on their own screen.
 *
 * That was latent while URL-only shipments were rare. OKS-984's queue asks the
 * operator for a tracking LINK as its single field and derives a number only
 * for recognised carriers, which makes "URL but no number" the common case
 * rather than the edge. We shipped the thing that promotes it, so the fix
 * belongs here and not in a follow-up.
 *
 * The `Track →` link opens in a new tab with `rel="noopener noreferrer"`.
 */
interface TrackingBlockProps {
  trackingNumber: string | null
  trackingUrl: string | null | undefined
  deliveryEta: string | null | undefined
  locale: Locale
}

export function TrackingBlock({
  trackingNumber,
  trackingUrl,
  deliveryEta,
  locale,
}: TrackingBlockProps) {
  const { t } = useTranslation()

  // ⚠️ Branch on "is there anything to show at all", not on the number alone.
  // `trackingUrl` is sufficient on its own — see the OQ-8 note above.
  if (!trackingNumber && !trackingUrl) {
    return (
      <div
        className="bg-muted/40 text-muted-foreground rounded-xl p-3 text-sm"
        data-testid="gifting-tracking-none"
      >
        {t("creator_mission_detail.gifting.tracking_none")}
      </div>
    )
  }

  const eta = formatEta(deliveryEta, locale)

  return (
    <div
      className="bg-muted/40 flex flex-col gap-2 rounded-xl p-3"
      data-testid="gifting-tracking-block"
    >
      <div className="flex items-start gap-2">
        <Truck
          className="text-muted-foreground mt-0.5 size-4 shrink-0"
          aria-hidden="true"
        />
        <div className="flex min-w-0 flex-col">
          <span
            className="text-sm font-semibold"
            data-testid="gifting-tracking-number"
          >
            {trackingNumber
              ? t("creator_mission_detail.gifting.tracking_number", {
                  number: trackingNumber,
                })
              : t("creator_mission_detail.gifting.shipped_no_number")}
          </span>
          {eta && (
            <span className="text-muted-foreground text-xs">
              {t("creator_mission_detail.gifting.arrives", { date: eta })}
            </span>
          )}
        </div>
        {trackingUrl && (
          <a
            href={trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary ml-auto shrink-0 text-xs font-medium"
            data-testid="gifting-tracking-link"
          >
            {t("creator_mission_detail.gifting.track")}
          </a>
        )}
      </div>

      {trackingUrl && (
        <Button
          asChild
          variant="outline"
          size="sm"
          className="self-start"
          data-testid="gifting-tracking-button"
        >
          <a href={trackingUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink aria-hidden="true" />
            {t("creator_mission_detail.gifting.track_package")}
          </a>
        </Button>
      )}
    </div>
  )
}

/** Locale-aware short date from an ISO `YYYY-MM-DD`; null/invalid → null. */
function formatEta(
  iso: string | null | undefined,
  locale: Locale,
): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString(locale === "fr" ? "fr-FR" : "en-US", {
    day: "2-digit",
    month: "short",
  })
}
